import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring, animate, useMotionValue, useInView, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Display, Heading, Body, Label } from '../components/Typography';
import Button from '../components/Button';
import VisualSystem from '../components/VisualSystem';

const MethodStep = ({ step, idx, progress, isDesktop }) => {
  const start = 0.1 + (idx * 0.2);
  const end = 0.1 + ((idx + 1) * 0.2);
  
  // Desktop-specific scroll-linked opacity
  const desktopOpacity = useTransform(progress, 
    [start - 0.1, start, end, end + 0.1], 
    [0.2, 1, 1, 0.2]
  );
  
  const desktopX = useTransform(progress,
    [start - 0.1, start, end, end + 0.1],
    [-10, 0, 0, -10]
  );

  return (
    <motion.div 
      style={isDesktop ? { opacity: desktopOpacity, x: desktopX } : { opacity: 1, x: 0 }}
      className="relative pl-8 md:pl-12 border-l border-epoq-blue/20"
    >
      <span className="absolute -left-4 top-0 bg-epoq-paper px-2 font-serif text-xl text-epoq-blue/40">{step.num}</span>
      <h3 className="font-serif text-3xl lg:text-4xl mb-4 tracking-tight">{step.title}</h3>
      <p className="font-sans text-lg lg:text-xl opacity-80 leading-relaxed max-w-md">{step.desc}</p>
    </motion.div>
  );
};

const MethodSection = () => {
  const containerRef = useRef(null);
  const mobileAnimRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(true);
  const [hasPlayedMobile, setHasPlayedMobile] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  
  const mobileProgress = useMotionValue(prefersReducedMotion ? 3 : 0);
  const isMobileInView = useInView(mobileAnimRef, { amount: 0.5 });

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mobile Auto-play Logic
  useEffect(() => {
    if (!isDesktop && isMobileInView && !hasPlayedMobile && !prefersReducedMotion) {
      setHasPlayedMobile(true);
      animate(mobileProgress, 3, { duration: 6, ease: "easeInOut" });
    }
  }, [isMobileInView, isDesktop, hasPlayedMobile, prefersReducedMotion]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const smoothProgress = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });
  const visualProgress = useTransform(smoothProgress, [0.1, 0.9], [0, 3]);

  const steps = [
    { id: 'understand', num: '01', title: 'UNDERSTAND', desc: 'Build the clarity needed to assess opportunities, risks and priorities.' },
    { id: 'apply', num: '02', title: 'APPLY', desc: 'Turn new knowledge into better everyday practices and working methods.' },
    { id: 'govern', num: '03', title: 'GOVERN', desc: 'Create the principles, responsibilities and policies needed for responsible use.' },
    { id: 'implement', num: '04', title: 'IMPLEMENT', desc: 'Translate learning into improved workflows, measurable outcomes and sustained change.' },
  ];

  return (
    <section 
      ref={containerRef} 
      className="relative bg-epoq-paper md:h-[400vh]"
    >
      {/* 
        We use a single render tree with CSS-responsive classes. 
        This prevents the "blank screen" hydration mismatch in Lighthouse.
      */}
      <div className="md:sticky md:top-0 md:h-screen md:overflow-hidden w-full">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center min-h-screen py-20 px-6 md:px-12">
          
          {/* Left Column: Heading & Steps */}
          <div className="w-full md:w-1/2 flex flex-col justify-center z-10">
            <div className="mb-12 md:mb-20">
              <Heading>From knowledge <br /><span className="italic text-epoq-blue/70">to action.</span></Heading>
            </div>
            
            {/* Mobile Animation: Positioned between Heading and Steps on small screens */}
            <div ref={mobileAnimRef} className="block md:hidden w-full aspect-square mb-12 relative bg-epoq-blue shadow-xl overflow-hidden">
              <VisualSystem progress={mobileProgress} isStatic={prefersReducedMotion} />
            </div>

            <div className="relative space-y-12 md:space-y-20">
              {steps.map((step, idx) => (
                <MethodStep 
                  key={idx} 
                  step={step} 
                  idx={idx} 
                  progress={smoothProgress} 
                  isDesktop={isDesktop} 
                />
              ))}
            </div>
          </div>

          {/* Right Column: Desktop Visual (Hidden on mobile) */}
          <div className="hidden md:flex w-1/2 h-full items-center justify-center p-12">
            <div className="w-full max-w-xl aspect-square relative">
              <VisualSystem progress={visualProgress} isStatic={prefersReducedMotion} />
              <div className="absolute top-4 left-4 bg-epoq-blue/10 backdrop-blur-sm px-3 py-1 text-[10px] font-bold tracking-widest uppercase">
                Method Evolution
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

// ... Rest of Home components (Hero, Position, etc.) remain unchanged
const Home = () => {
  return (
    <div className="w-full">
      <HeroSection />
      <PositionSection />
      <MethodSection />
      <FirstProgrammesSection />
      <ComingNextSection />
      <ForOrgsSection />
      <FinalCTASection />
    </div>
  );
};

const HeroSection = () => (
  <section className="relative min-h-[90vh] flex flex-col justify-center px-6 lg:px-12 overflow-hidden border-b border-epoq-blue/20">
    <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[linear-gradient(to_right,#011844_1px,transparent_1px),linear-gradient(to_bottom,#011844_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
    <div className="max-w-7xl mx-auto w-full relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end pb-20">
      <div className="lg:col-span-12 relative">
        <Label className="block mb-8 text-epoq-coral">The Living Syllabus</Label>
        <Display className="max-w-[4xl] relative z-10">
          <span className="block">Knowledge is</span>
          <span className="block text-right lg:ml-32 italic text-epoq-blue/90">only the beginning.</span>
        </Display>
      </div>
      <div className="lg:col-span-5 lg:col-start-8 mt-12 lg:mt-0">
        <div>
          <Body large className="mb-10 font-medium text-epoq-blue">
            EPOQ Learn helps organisations turn understanding into practical capability, responsible action and measurable business value.
          </Body>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button to="/programmes" variant="primary">Explore programmes</Button>
            <Button to="/for-organisations" variant="outline">For organisations</Button>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const PositionSection = () => (
  <section className="py-32 px-6 lg:px-12 bg-epoq-blue text-epoq-white overflow-hidden relative">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16">
      <div className="lg:col-span-4 relative">
        <div className="md:sticky md:top-32">
          <Label className="text-epoq-lime mb-6 block">A Clear Position</Label>
          <Heading className="text-epoq-paper">Learning built for real work.</Heading>
        </div>
      </div>
      <div className="lg:col-span-7 lg:col-start-6 space-y-12">
        <p className="font-serif text-3xl lg:text-4xl leading-relaxed text-epoq-paper">
          EPOQ Learn is a B2B learning and capability platform for organisations navigating new technologies, changing ways of working and growing demands for responsible implementation.
        </p>
        <div className="h-px w-full bg-epoq-white/20"></div>
        <p className="font-sans text-xl opacity-80 leading-relaxed">
          Our programmes begin with real decisions, real roles and real workflows. <br /><br />
          The aim is not simply to transfer information. <span className="block mt-6 text-2xl font-serif text-epoq-lime italic">It is to build the capability to act.</span>
        </p>
      </div>
    </div>
  </section>
);

const FirstProgrammesSection = () => (
  <section className="py-32 px-6 lg:px-12 bg-epoq-blue text-epoq-white">
    <div className="max-w-7xl mx-auto mb-20 lg:mb-32 grid lg:grid-cols-2 gap-12">
      <div>
        <Label className="text-epoq-lime mb-6 block">The first programmes</Label>
        <Heading className="text-epoq-paper">Building capability <br />in AI.</Heading>
      </div>
      <div className="flex items-end">
        <p className="text-xl opacity-80 leading-relaxed font-sans max-w-md">
          Our initial programmes focus on one of the most urgent capability challenges facing organisations today: using artificial intelligence with clarity, responsibility and practical purpose.
        </p>
      </div>
    </div>
    <div className="max-w-7xl mx-auto space-y-8">
      {[
        { num: '01', title: 'AI for Leadership and Boards', tags: ['Leadership', 'Strategy', 'Governance'], desc: 'A strategic and practical programme for leaders and board members who need to prioritise AI initiatives, understand risk, place responsibility and define a realistic direction.', link: '/programmes/ai-leadership-boards' },
        { num: '02', title: 'Generative AI for Everyday Knowledge Work', tags: ['Professional practice', 'Productivity', 'Responsible use'], desc: 'A hands-on programme for professionals and teams who want to use generative AI safely and effectively in research, documents, communication, analysis, meetings and planning.', link: '/programmes/gen-ai-everyday' }
      ].map((prog, i) => (
        <div key={i} className="group border-t border-b border-epoq-white/20 hover:border-epoq-lime transition-colors duration-500 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-epoq-lime/0 group-hover:bg-epoq-lime/5 transition-colors duration-500 pointer-events-none"></div>
          <div className="lg:col-span-2"><span className="font-serif text-xl opacity-50">{prog.num}</span></div>
          <div className="lg:col-span-5">
            <h3 className="font-serif text-3xl lg:text-5xl mb-6 leading-tight group-hover:text-epoq-lime transition-colors">{prog.title}</h3>
            <div className="flex flex-wrap gap-2 mb-6">
              {prog.tags.map(t => <span key={t} className="text-xs uppercase tracking-wider border border-epoq-white/30 px-3 py-1 rounded-full opacity-70">{t}</span>)}
            </div>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex flex-col justify-between">
            <p className="opacity-80 mb-8 leading-relaxed">{prog.desc}</p>
            <Button to={prog.link} variant="secondary" className="self-start">Explore the programme</Button>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const ComingNextSection = () => (
  <section className="py-24 px-6 lg:px-12 border-b border-epoq-blue/20">
    <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-16">
      <div className="lg:w-1/3"><Heading className="md:sticky md:top-32">Building the full <br />capability journey.</Heading></div>
      <div className="lg:w-2/3 space-y-16">
        {[
          { num: '03', title: 'Responsible AI, the EU AI Act and Your Company AI Policy', desc: 'A practical governance programme connecting AI literacy, internal policy, responsibilities, data principles and organisational practice.' },
          { num: '04', title: 'From AI Experiment to Measurable Business Value', desc: 'A team-based programme that turns isolated AI experiments into prioritised use cases, redesigned workflows, meaningful KPIs and an implementation roadmap.' }
        ].map(prog => (
          <div key={prog.num} className="border border-epoq-blue/10 bg-white p-8 lg:p-12">
            <Label className="text-epoq-coral mb-4 block">In development — {prog.num}</Label>
            <h4 className="font-serif text-3xl mb-4">{prog.title}</h4>
            <p className="opacity-80 leading-relaxed max-w-lg mb-8">{prog.desc}</p>
            <Button to="/contact" variant="outline">Register your interest</Button>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ForOrgsSection = () => (
  <section className="py-32 px-6 lg:px-12 bg-epoq-paper relative overflow-hidden">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <div>
        <Heading className="mb-8">Learning shaped around your organisation.</Heading>
        <div className="space-y-6 text-lg opacity-80 max-w-md mb-12">
          <p>EPOQ Learn offers both open programmes and company-specific learning experiences.</p>
          <p>Closed programmes can be adapted to your organisation’s roles, workflows, industry context and internal policies.</p>
        </div>
        <Button to="/contact" variant="primary">Discuss a company programme</Button>
      </div>
      <div className="relative">
        <div className="aspect-square bg-epoq-blue text-epoq-white p-12 flex flex-col justify-between">
          <div className="font-serif text-2xl italic opacity-50 mb-8">Format Variables</div>
          <div className="flex flex-wrap gap-4">
            {['Live online', 'In-person workshops', 'Closed cohorts', 'Executive briefings', 'Role-specific', 'Templates', 'Follow-up', 'Sprints'].map((f, i) => (
              <div key={i} className={`border border-epoq-white/20 px-4 py-2 text-sm ${i % 3 === 0 ? 'bg-epoq-lime text-epoq-blue border-transparent font-medium' : ''}`}>{f}</div>
            ))}
          </div>
          <div className="mt-12 h-px w-full bg-gradient-to-r from-epoq-lime to-transparent"></div>
        </div>
      </div>
    </div>
  </section>
);

const FinalCTASection = () => (
  <section className="py-32 px-6 lg:px-12 bg-epoq-blue text-epoq-white text-center border-t border-epoq-white/20">
    <div className="max-w-4xl mx-auto">
      <div className="text-sm font-bold tracking-widest text-epoq-coral mb-8 uppercase flex items-center justify-center gap-4">
        <span>Learn</span> <span className="opacity-50">→</span> <span>Apply</span> <span className="opacity-50">→</span> <span>Implement</span>
      </div>
      <Display className="mb-8 text-epoq-paper">What could your organisation be ready to do next?</Display>
      <p className="text-xl opacity-80 mb-12 max-w-2xl mx-auto">Talk to us about leadership development, team capability or a company-specific AI programme.</p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button to="/programmes" variant="secondary">Explore programmes</Button>
        <Button to="/contact" variant="outline" className="border-epoq-white text-epoq-white hover:bg-epoq-white hover:text-epoq-blue">Start a conversation</Button>
      </div>
    </div>
  </section>
);

export default Home;